import type { SyncBackend, SyncBackendKind, SyncManifest, SyncRecord } from "../ports/sync.js";
import { DocumentSyncBackend, emptySyncDocument, isSyncDocument, type SyncDocument } from "./backend.js";

export const DEFAULT_SYNC_DOCUMENT_PATH = "roqeet-sync.json";

/**
 * Minimal object-store contract for direct-to-provider browser/app storage.
 * Implementations talk to the user's provider directly; RoQeet servers should
 * only store metadata such as provider id, display name, and last sync time.
 */
export interface RemoteObjectStore {
  readonly kind: SyncBackendKind;
  readText(path: string): Promise<string | undefined>;
  writeText(path: string, text: string, contentType?: string): Promise<void>;
  exists(path: string): Promise<boolean>;
  readBlob?(path: string): Promise<Blob | undefined>;
  writeBlob?(path: string, blob: Blob, contentType?: string): Promise<void>;
}

export interface RemoteObjectStoreSyncBackendOptions {
  documentPath?: string;
  kind?: SyncBackendKind;
}

/**
 * SyncBackend adapter over a provider object store. The sync engine still sees
 * one validated SyncBackend; provider-specific auth, paths, CORS, and APIs stay
 * behind RemoteObjectStore.
 */
export class RemoteObjectStoreSyncBackend implements SyncBackend {
  readonly kind: SyncBackendKind;
  private readonly documentPath: string;
  private inner: DocumentSyncBackend = new DocumentSyncBackend();
  private loaded = false;

  constructor(
    private readonly store: RemoteObjectStore,
    options: RemoteObjectStoreSyncBackendOptions = {},
  ) {
    this.kind = options.kind ?? store.kind;
    this.documentPath = options.documentPath ?? DEFAULT_SYNC_DOCUMENT_PATH;
  }

  async load(): Promise<void> {
    const text = await this.store.readText(this.documentPath);
    let doc: SyncDocument = emptySyncDocument();
    if (text) {
      try {
        const parsed = JSON.parse(text);
        if (isSyncDocument(parsed)) doc = parsed;
      } catch {
        // Corrupt remote data should not make the app unusable. A later flush
        // rewrites a valid empty/merged document.
      }
    }
    this.inner = new DocumentSyncBackend(doc);
    this.loaded = true;
  }

  async flush(): Promise<void> {
    if (!this.loaded) throw new Error("RemoteObjectStoreSyncBackend.load() must run before flush().");
    await this.store.writeText(
      this.documentPath,
      JSON.stringify(this.inner.toDocument()),
      "application/json",
    );
  }

  pullManifest(): Promise<SyncManifest> {
    return this.inner.pullManifest();
  }

  pullRecords(ids: string[]): Promise<SyncRecord[]> {
    return this.inner.pullRecords(ids);
  }

  pushRecords(records: SyncRecord[]): Promise<void> {
    return this.inner.pushRecords(records);
  }

  pullBlob(hash: string): Promise<Blob | undefined> {
    return this.inner.pullBlob(hash);
  }

  pushBlob(hash: string, blob: Blob): Promise<void> {
    return this.inner.pushBlob(hash, blob);
  }

  hasBlob(hash: string): Promise<boolean> {
    return this.inner.hasBlob(hash);
  }
}
